/**
 * Token Encryption Utility
 * 
 * COPPA/GDPR Compliance: Encrypts sensitive auth tokens stored in localStorage
 * to protect against XSS attacks and local storage inspection.
 * 
 * Uses Web Crypto API with AES-GCM for authenticated encryption.
 * The encryption key is stored in sessionStorage (cleared on tab close).
 */

const ENCRYPTION_KEY_NAME = 'cpb_token_key';
const ALGORITHM = { name: 'AES-GCM', length: 256 };

/**
 * Get or create the encryption key
 * Key is stored in sessionStorage so it's cleared when browser closes
 */
async function getOrCreateEncryptionKey() {
    try {
        const storedKey = sessionStorage.getItem(ENCRYPTION_KEY_NAME);
        
        if (storedKey) {
            // Import existing key
            const keyData = JSON.parse(storedKey);
            return await crypto.subtle.importKey(
                'jwk',
                keyData,
                ALGORITHM,
                true,
                ['encrypt', 'decrypt']
            );
        }
        
        // Generate new key
        const newKey = await crypto.subtle.generateKey(
            ALGORITHM,
            true,
            ['encrypt', 'decrypt']
        );
        
        // Export and store key
        const exported = await crypto.subtle.exportKey('jwk', newKey);
        sessionStorage.setItem(ENCRYPTION_KEY_NAME, JSON.stringify(exported));
        
        return newKey;
    } catch (error) {
        console.warn('[TokenEncryption] Crypto API not available, falling back to obfuscation');
        return null;
    }
}

/**
 * Encrypt a token string
 * @param {string} token - The token to encrypt
 * @returns {Promise<string>} - Base64 encoded encrypted data with IV
 */
export async function encryptToken(token) {
    if (!token) return null;
    
    try {
        const key = await getOrCreateEncryptionKey();
        
        if (!key) {
            // Fallback: simple obfuscation (not cryptographically secure, but better than plaintext)
            return 'OBF:' + btoa(token.split('').reverse().join(''));
        }
        
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encodedToken = new TextEncoder().encode(token);
        
        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            encodedToken
        );
        
        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(encrypted), iv.length);
        
        return 'ENC:' + btoa(String.fromCharCode(...combined));
    } catch (error) {
        console.error('[TokenEncryption] Encryption failed:', error);
        // Don't store unencrypted tokens
        return null;
    }
}

/**
 * Decrypt a token string
 * @param {string} encryptedData - Base64 encoded encrypted data with IV prefix
 * @returns {Promise<string|null>} - Decrypted token or null if decryption fails
 */
export async function decryptToken(encryptedData) {
    if (!encryptedData) return null;
    
    try {
        // Handle obfuscated tokens (fallback format)
        if (encryptedData.startsWith('OBF:')) {
            const obfuscated = encryptedData.slice(4);
            return atob(obfuscated).split('').reverse().join('');
        }
        
        // Handle legacy unencrypted tokens (migration)
        if (!encryptedData.startsWith('ENC:')) {
            console.warn('[TokenEncryption] Legacy unencrypted token detected, will re-encrypt on next save');
            return encryptedData;
        }
        
        const key = await getOrCreateEncryptionKey();
        
        if (!key) {
            console.warn('[TokenEncryption] Cannot decrypt: key not available');
            return null;
        }
        
        // Decode base64
        const b64Data = encryptedData.slice(4);
        const combined = Uint8Array.from(atob(b64Data), c => c.charCodeAt(0));
        
        // Extract IV and encrypted data
        const iv = combined.slice(0, 12);
        const encrypted = combined.slice(12);
        
        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            encrypted
        );
        
        return new TextDecoder().decode(decrypted);
    } catch (error) {
        console.error('[TokenEncryption] Decryption failed:', error);
        return null;
    }
}

/**
 * Check if a token is in encrypted format
 * @param {string} token - The token to check
 * @returns {boolean} - True if token is encrypted or obfuscated
 */
export function isEncryptedToken(token) {
    if (!token) return false;
    return token.startsWith('ENC:') || token.startsWith('OBF:');
}

/**
 * Clear the encryption key (for logout)
 */
export function clearEncryptionKey() {
    sessionStorage.removeItem(ENCRYPTION_KEY_NAME);
}

export default {
    encryptToken,
    decryptToken,
    isEncryptedToken,
    clearEncryptionKey
};
